"use client";

import styles from './_androstenedione.module.scss';
import Button from '../../../../components/button';
import CustomImage from '@/components/image';
import Modal from '@/components/modal';
import { useMemo, useRef } from 'react';
import HcpClinicalInformationAndrostenedioneModal from './androstenedione-modal';
import PageAnchor from '@/components/page-anchor';

export default function HcpClinicalInformationAndrostenedione({ index = 0 }) {
  const modalRef = useRef(null);

  const graph1Src = useMemo(() => {
    let src = '/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Reduced-Androstenedione-Levels.svg';

    if (index === 1) {
      src = '/images/page/hcp/clinical-information/Clinical-Information_Adult-Reduced-Androstenedione-Levels.svg';
    } 

    return src;
  }, [index]);

  const graph2Src = useMemo(() => {
    let src = '/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Reduced-17-OHP-Levels.svg'; 
    if (index === 1) {
      src = '/images/page/hcp/clinical-information/Clinical-Information_Adult-Reduced-17-OHP-Levels.svg';
    }

    return src;
  }, [index]);

  return (
    <>
      <PageAnchor anchorId="trial-1-efficacy-androstenedione" offsetY={65} offsetYLgOnly={true} />
      <PageAnchor anchorId="trial-2-efficacy-androstenedione" offsetY={65} offsetYLgOnly={true} />
      <section className={`${styles.container} ${index === 1 ? styles.container__child : ''}`}>
        <div className="container">
          <h2 className='page-heading h1'>
            {index === 0 ? (
              <>
                Androstenedione and <span className="text-nowrap">17-OHP:</span><br className="hidden mobile:inline-block" /> significantly reduced with CRENESSITY <span className="text-nowrap">at week 4<sup>1,9</sup></span>
                
              </>
            ) : (
              <>
                Androstenedione and <span className="text-nowrap">17-OHP:</span><br className="hidden mobile:inline-block" /> reduced with CRENESSITY <span className="text-nowrap">at week 4<sup>1,10,25</sup></span>
              </>
            )}
          </h2>

          <div className={styles.graphsWrapper}>
            <div className={styles.graphSection}>
              <p className={styles.subheading}>
                {index === 0 ? (
                  <>Androstenedione (change from baseline)<sup>1,9</sup><span className="super-asterisc">*</span><sup>&dagger;</sup></>
                ) : (
                  <>Androstenedione (change from baseline)<sup>1,10</sup><span className="super-asterisc">*</span><sup>&dagger;</sup></>
                )}
              </p>
              <div className={styles.graphContainer}>
                <CustomImage
                  src={graph1Src}
                  alt={index === 0 
                    ? "CRENESSITY reduced androstenedione levels by 197 ng/dL vs a 71 ng/dL increase with placebo, an ~4x greater improvement."
                    : "CRENESSITY reduced androstenedione levels by 299 ng/dL vs a 46 ng/dL increase with placebo, an ~8x greater improvement."
                  }
                  width={540}
                  height={540}
                />
              </div>
            </div>

            <div className={styles.graphSection}>
              <p className={styles.subheading}>
                {index === 0 ? (
                  <>17-OHP (change from baseline)<sup>9</sup><span className="super-asterisc">*</span><sup>‡</sup></>
                ) : (
                  <>17-OHP (change from baseline)<sup>10,25</sup><span className="super-asterisc">*</span><sup>‡</sup></>
                )}
              </p>
              <div className={styles.graphContainer}>
                <CustomImage
                  src={graph2Src}
                  alt={index === 0
                    ? "CRENESSITY reduced 17-OHP levels by 5865 ng/dL vs a 556 ng/dL increase with placebo, an ~12x greater improvement."
                    : "CRENESSITY reduced 17-OHP levels by 5994 ng/dL vs a 156 ng/dL decrease with placebo, an ~37x greater improvement."
                  }
                  width={540}
                  height={540}
                />
              </div>
            </div>
          </div>

          {index === 1 && <p className={styles.purpleText}>
            Unlike androstenedione, the 17-OHP endpoint was not adjusted for multiplicity; these descriptive results could represent chance findings.<sup>25</sup>
          </p>}

          <div>
            <ul className="footnotes">
              {index === 0 ? (
                <>
                  <li><span className="hang">*</span><span>Measurements were taken pre-morning GC dose.<sup>10</sup></span></li>
                  <li><sup className="hang">†</sup><span>Mean baseline androstenedione levels were 431 ± 461 ng/dL for all participants, 483&nbsp;±&nbsp;456&nbsp;ng/dL for placebo, and 405 ± 464 ng/dL for CRENESSITY.<sup>10</sup></span></li>
                  <li><sup className="hang">‡</sup><span>Mean baseline 17-OHP levels were 8682 ± 6847 ng/dL for all participants, 9026 ± 5563 ng/dL for placebo, and 8513 ± 7431 ng/dL for CRENESSITY.<sup>10</sup></span></li>
                </>
              ) : (
                <>
                  <li><span className="hang">*</span><span>Measurements were taken pre-morning GC dose.<sup>10</sup></span></li>
                  <li><sup className="hang">†</sup><span>Mean baseline androstenedione levels were 620 ± 729 ng/dL for all participants, 590 ± 572 ng/dL for placebo, and 634 ± 796 ng/dL for CRENESSITY.<sup>1,10</sup></span></li>
                  <li><sup className="hang">‡</sup><span>Mean baseline 17-OHP levels were 9467 ± 8829 ng/dL for all participants, 9787 ± 9435 ng/dL for placebo, and 9314 ± 8560 ng/dL for CRENESSITY.<sup>10</sup></span></li>
                </>
              )}
            </ul>
          </div>

          <div className={styles.ctaWrapper}>
            <Button
              type="button"
              variant={['outline--eggplant', 'hcp-big']} 
              onClick={() => modalRef.current?.open()}
              data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="View androgen-related symptoms"
            >
              View androgen-related symptoms
            </Button>
          </div>
              
          {index === 0 && (<>
            <div className="hcp-callout">
              <strong>96%</strong> of caregivers who completed the open-label extension phase of the trial wished their child could have started CRENESSITY at a younger age.<sup>14</sup>*
            </div>
            <ul className={`footnotes ${styles.footnotes}`}>
              <li>
                <span className="hang">*</span>
                <span>
                  Percentage based on caregivers of pediatric participants (n=27/29) from the CAHtalyst<sup>&reg;</sup> trials who &ldquo;agreed&rdquo; or &ldquo;strongly agreed&rdquo; to the statement, &ldquo;I wish my child could have started CRENESSITY at a younger age,&rdquo; in the final treatment satisfaction survey.<sup>14</sup>
                </span>
              </li>
            </ul>
          </>)}
        </div>
      </section>
      <Modal ref={modalRef} customClass={styles.modal}>
        <HcpClinicalInformationAndrostenedioneModal index={index} />
      </Modal>
    </>
  );
}
